import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Container, Row, Button, Stack } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { isNull, isUndefined } from 'lodash';

import { AmountControl, FormGroupControl, FormGroupSelect, Loading, LoadingError } from '@components/Base';
import { CarPlanTypes } from '@models/CarPlan';

import { useCarQuoteFetch } from '../hook';
import { carQuoteCustom, CarQuoteCustomPayload } from '../action';
import { RightCol } from '../right-col';
import { FormValues } from './type';
import { useInitialValues, useValidationSchema } from './hook';

const GROUP_PROPS = { className: 'mb-3' };

const CarQuoteBuyCustomPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const carQuote = useCarQuoteFetch();
  const initialValues = useInitialValues();
  const validationSchema = useValidationSchema();

  if (isUndefined(carQuote)) return <Loading />;
  if (isNull(carQuote)) return <LoadingError />;
  if (!initialValues) return null;

  const { planType, accidentToDriver, passengerLiability, flood, srcc, orderNumber } = carQuote;

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: CarQuoteCustomPayload = { ...values, orderNumber };
    dispatch(carQuoteCustom(payload));
    setSubmitting(false);
  };

  return (
    <Container className="my-3">
      <Row className="gy-5">
        <Col lg={8}>
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
            {({ isSubmitting, values }) => (
              <Form>
                <Card>
                  <Card.Body>
                    <FormGroupSelect name="customType" label="Custom type" required groupProps={GROUP_PROPS}>
                      <option value="no_custom">No Custom</option>
                      <option value="custom">Custom</option>
                      <option value="lump_sum">Lump sum</option>
                    </FormGroupSelect>
                    <AmountControl
                      title={t('common:tpl')}
                      premiumName="thirdPartyAmount"
                      discountName="thirdPartyDiscountAmount"
                      groupProps={GROUP_PROPS}
                      disabled={values.customType !== 'custom'}
                    />
                    {planType === CarPlanTypes.Comprehensive && (
                      <AmountControl
                        title={t('common:od')}
                        premiumName="ownDamageAmount"
                        discountName="ownDamageDiscountAmount"
                        groupProps={GROUP_PROPS}
                        disabled={values.customType !== 'custom'}
                      />
                    )}
                    <AmountControl
                      title="Third-party deductible"
                      premiumName="thirdPartyDeductibleAmount"
                      discountName="thirdPartyDeductibleDiscountAmount"
                      groupProps={GROUP_PROPS}
                      disabled={values.customType === 'no_custom'}
                    />
                    {planType === CarPlanTypes.Comprehensive && (
                      <>
                        <AmountControl
                          title="Own damage deductible"
                          premiumName="ownDamageDeductibleAmount"
                          discountName="ownDamageDeductibleDiscountAmount"
                          groupProps={GROUP_PROPS}
                          disabled={values.customType === 'no_custom'}
                        />
                        <AmountControl
                          title="Theft deductible"
                          premiumName="theftDeductibleAmount"
                          discountName="theftDeductibleDiscountAmount"
                          groupProps={GROUP_PROPS}
                          disabled={values.customType === 'no_custom'}
                        />
                      </>
                    )}
                    {accidentToDriver && (
                      <AmountControl
                        title={t('common:ad')}
                        premiumName="accidentToDriverAmount"
                        discountName="accidentToDriverDiscountAmount"
                        groupProps={GROUP_PROPS}
                        disabled={values.customType !== 'custom'}
                      />
                    )}
                    {passengerLiability && (
                      <AmountControl
                        title={t('common:pl')}
                        premiumName="passengerLiabilityAmount"
                        discountName="passengerLiabilityDiscountAmount"
                        groupProps={GROUP_PROPS}
                        disabled={values.customType !== 'custom'}
                      />
                    )}

                    {flood && (
                      <AmountControl
                        title={t('common:flood')}
                        premiumName="floodAmount"
                        groupProps={GROUP_PROPS}
                        disabled={values.customType !== 'custom'}
                      />
                    )}
                    {srcc && (
                      <AmountControl
                        title={t('common:srcc')}
                        premiumName="srccAmount"
                        groupProps={GROUP_PROPS}
                        disabled={values.customType !== 'custom'}
                      />
                    )}
                    {planType === CarPlanTypes.Comprehensive && (
                      <FormGroupControl
                        name="idvAmount"
                        label="IDV Amount"
                        required
                        groupProps={GROUP_PROPS}
                        type="number"
                        disabled={values.customType === 'no_custom'}
                      />
                    )}
                    <FormGroupControl
                      name="premium"
                      label="Premium"
                      required
                      groupProps={GROUP_PROPS}
                      type="number"
                      disabled={values.customType !== 'lump_sum'}
                    />
                    <FormGroupControl
                      name="adminFeeAmount"
                      label="Admin fee"
                      required
                      groupProps={GROUP_PROPS}
                      type="number"
                      disabled={values.customType === 'no_custom'}
                    />
                    <FormGroupControl
                      required
                      name="noClaimDiscountPercentage"
                      label="No claim discount (%)"
                      groupProps={GROUP_PROPS}
                      type="number"
                    />
                    <FormGroupControl
                      required
                      name="loyaltyDiscountPercentage"
                      label="Loyalty discount (%)"
                      groupProps={GROUP_PROPS}
                      type="number"
                    />
                    <FormGroupControl required name="proof" type="file" label={t('proof')} groupProps={GROUP_PROPS} />
                    <FormGroupControl
                      required
                      name="note"
                      label="Note"
                      groupProps={GROUP_PROPS}
                      as="textarea"
                      rows={4}
                    />
                  </Card.Body>

                  <Card.Footer className="bg-white border-0 mx-auto my-4">
                    <Stack direction="horizontal" gap={3}>
                      <Button onClick={() => navigate(-1)} variant="outline-primary" size="lg">
                        {t('common:cancel')}
                      </Button>

                      <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
                        {t('common:update')}
                      </Button>
                    </Stack>
                  </Card.Footer>
                </Card>
              </Form>
            )}
          </Formik>
        </Col>

        <RightCol carQuote={carQuote} />
      </Row>
    </Container>
  );
};

export { CarQuoteBuyCustomPage };
